import { useTranslation } from "react-i18next"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SearchableSelect } from "@/components/SearchableSelect"
import { FormError } from "@/components/FormError"
import { TagMultiSelect } from "@/components/TagMultiSelect"
import type { AssetFormData, SelectOption } from "@/types/inventory"
import type { AssetType } from "@/lib/assetTypes"

interface AssetFormProps {
  data: AssetFormData
  errors: Record<string, string | string[]>
  onChange: <K extends keyof AssetFormData>(field: K, value: AssetFormData[K]) => void
  idPrefix: string
  assetType: AssetType
  stages: SelectOption[]
  customers: SelectOption[]
  suppliers: SelectOption[]
  locations: SelectOption[]
  tags: SelectOption[]
  products?: SelectOption[]
  containers?: SelectOption[]
  disabledFields?: Array<keyof AssetFormData>
  hiddenFields?: Array<keyof AssetFormData>
}

export function AssetForm({
  data,
  errors,
  onChange,
  idPrefix,
  assetType,
  stages,
  customers,
  suppliers,
  locations,
  tags,
  products = [],
  containers = [],
  disabledFields = [],
  hiddenFields = [],
}: AssetFormProps) {
  const { t } = useTranslation()
  const isDisabled = (field: keyof AssetFormData) => disabledFields.includes(field)
  const isHidden = (field: keyof AssetFormData) => hiddenFields.includes(field)

  return (
    <div className="space-y-4 py-4">
      {/* Row 1: Code and Stage */}
      <div className={`grid grid-cols-1 ${!isHidden("stage_id") ? "sm:grid-cols-2" : ""} gap-4`}>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-code`}>{t("inventory.form.codeLabel")} *</Label>
          <Input
            id={`${idPrefix}-code`}
            value={data.code}
            onChange={(e) => onChange("code", e.target.value)}
            placeholder={t("inventory.form.codePlaceholder")}
          />
          <FormError error={errors.code} />
        </div>
        {!isHidden("stage_id") && (
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-stage`}>{t("inventory.form.stageLabel")} *</Label>
            <Select
              value={data.stage_id}
              onValueChange={(value) => onChange("stage_id", value)}
              disabled={isDisabled("stage_id")}
            >
              <SelectTrigger id={`${idPrefix}-stage`} className="w-full">
                <SelectValue placeholder={t("inventory.filters.all")} />
              </SelectTrigger>
              <SelectContent>
                {stages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: stage.color }}
                      />
                      {stage.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormError error={errors.stage_id} />
          </div>
        )}
      </div>

      {/* Keg: Product and Container */}
      {assetType === "Keg" && (
        <>
          {(!isHidden("product_id") || !isHidden("container_id")) && (
            <div className={`grid grid-cols-1 ${!isHidden("product_id") && !isHidden("container_id") ? "sm:grid-cols-2" : ""} gap-4`}>
              {!isHidden("product_id") && (
                <div className="space-y-2">
                  <Label htmlFor={`${idPrefix}-product`}>{t("inventory.form.productLabel")}</Label>
                  <Select
                    value={data.product_id}
                    onValueChange={(value) => onChange("product_id", value === "_none" ? "" : value)}
                    disabled={isDisabled("product_id")}
                  >
                    <SelectTrigger id={`${idPrefix}-beer-style`} className="w-full">
                      <SelectValue placeholder={t("inventory.filters.all")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">{t("inventory.filters.all")}</SelectItem>
                      {products.map((bs) => (
                        <SelectItem key={bs.id} value={bs.id}>
                          {bs.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormError error={errors.product_id} />
                </div>
              )}
              {!isHidden("container_id") && (
                <div className="space-y-2">
                  <Label>{t("inventory.form.containerLabel")}</Label>
                  <SearchableSelect
                    value={data.container_id}
                    onChange={(value) => onChange("container_id", value)}
                    options={containers}
                    placeholder="-"
                    emptyLabel="-"
                  />
                  <FormError error={errors.container_id} />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Customer and Supplier (all types) */}
      {(!isHidden("customer_id") || !isHidden("supplier_id")) && (
        <div className={`grid grid-cols-1 ${!isHidden("customer_id") && !isHidden("supplier_id") ? "sm:grid-cols-2" : ""} gap-4`}>
          {!isHidden("customer_id") && (
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-customer`}>{t("inventory.form.customerLabel")}</Label>
              <Select
                value={data.customer_id}
                onValueChange={(value) => onChange("customer_id", value === "_none" ? "" : value)}
                disabled={isDisabled("customer_id")}
              >
                <SelectTrigger id={`${idPrefix}-customer`} className="w-full">
                  <SelectValue placeholder={t("inventory.filters.all")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{t("inventory.filters.all")}</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormError error={errors.customer_id} />
            </div>
          )}
          {!isHidden("supplier_id") && (
            <div className="space-y-2">
              <Label>{t("inventory.form.supplierLabel")}</Label>
              <SearchableSelect
                value={data.supplier_id}
                onChange={(value) => onChange("supplier_id", value)}
                options={suppliers}
                placeholder="-"
                emptyLabel="-"
              />
              <FormError error={errors.supplier_id} />
            </div>
          )}
        </div>
      )}

      {/* Location (all types) */}
      {!isHidden("location_id") && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-location`}>{t("inventory.form.locationLabel")}</Label>
            <Select
              value={data.location_id}
              onValueChange={(value) => onChange("location_id", value === "_none" ? "" : value)}
              disabled={isDisabled("location_id")}
            >
              <SelectTrigger id={`${idPrefix}-location`} className="w-full">
                <SelectValue placeholder={t("inventory.filters.all")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">{t("inventory.filters.all")}</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormError error={errors.location_id} />
          </div>
        </div>
      )}

      {/* Keg-specific: Bought At, Serial Number, Tare, Volume */}
      {assetType === "Keg" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-bought-at`}>{t("inventory.form.boughtAtLabel")} *</Label>
              <Input
                id={`${idPrefix}-bought-at`}
                type="date"
                value={data.bought_at}
                onChange={(e) => onChange("bought_at", e.target.value)}
              />
              <FormError error={errors.bought_at} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-serial-number`}>{t("inventory.form.serialNumberLabel")}</Label>
              <Input
                id={`${idPrefix}-serial-number`}
                value={data.serial_number}
                onChange={(e) => onChange("serial_number", e.target.value)}
                placeholder={t("inventory.form.serialNumberPlaceholder")}
              />
              <FormError error={errors.serial_number} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-batch-number`}>{t("inventory.form.batchNumberLabel")}</Label>
              <Input
                id={`${idPrefix}-batch-number`}
                value={data.batch_number}
                onChange={(e) => onChange("batch_number", e.target.value)}
                placeholder={t("inventory.form.batchNumberPlaceholder")}
              />
              <FormError error={errors.batch_number} />
            </div>
            {!isHidden("product_id") && data.product_id && (
              <div className="space-y-2">
                <Label htmlFor={`${idPrefix}-expiration-date`}>{t("inventory.form.expirationDateLabel")}</Label>
                <Input
                  id={`${idPrefix}-expiration-date`}
                  type="date"
                  value={data.expiration_date}
                  onChange={(e) => onChange("expiration_date", e.target.value)}
                />
                <FormError error={errors.expiration_date} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-tare`}>{t("inventory.form.tareLabel")}</Label>
              <Input
                id={`${idPrefix}-tare`}
                type="number"
                step="0.1"
                min="0"
                value={data.tare}
                onChange={(e) => onChange("tare", e.target.value)}
                placeholder={t("inventory.form.tarePlaceholder")}
              />
              <FormError error={errors.tare} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-volume`}>{t("inventory.form.volumeLabel")}</Label>
              <Input
                id={`${idPrefix}-volume`}
                type="number"
                step="0.1"
                min="0"
                value={data.volume}
                onChange={(e) => onChange("volume", e.target.value)}
                placeholder={t("inventory.form.volumePlaceholder")}
              />
              <FormError error={errors.volume} />
            </div>
          </div>
        </>
      )}

      {/* Fridge-specific: Brand, Model, Serial Number, Capacity */}
      {assetType === "Fridge" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-brand`}>{t("inventory.form.brandLabel")}</Label>
              <Input id={`${idPrefix}-brand`} value={data.brand} onChange={(e) => onChange("brand", e.target.value)} />
              <FormError error={errors.brand} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-model`}>{t("inventory.form.modelLabel")}</Label>
              <Input id={`${idPrefix}-model`} value={data.model} onChange={(e) => onChange("model", e.target.value)} />
              <FormError error={errors.model} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-serial-number`}>{t("inventory.form.serialNumberLabel")}</Label>
              <Input id={`${idPrefix}-serial-number`} value={data.serial_number} onChange={(e) => onChange("serial_number", e.target.value)} />
              <FormError error={errors.serial_number} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-capacity`}>{t("inventory.form.capacityLabel")}</Label>
              <Input id={`${idPrefix}-capacity`} type="number" min="0" value={data.capacity} onChange={(e) => onChange("capacity", e.target.value)} />
              <FormError error={errors.capacity} />
            </div>
          </div>
        </>
      )}

      {/* Cylinder-specific */}
      {assetType === "Cylinder" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-serial-number`}>{t("inventory.form.serialNumberLabel")}</Label>
              <Input id={`${idPrefix}-serial-number`} value={data.serial_number} onChange={(e) => onChange("serial_number", e.target.value)} />
              <FormError error={errors.serial_number} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-capacity`}>{t("inventory.form.capacityLabel")}</Label>
              <Input id={`${idPrefix}-capacity`} type="number" step="0.01" min="0" value={data.capacity} onChange={(e) => onChange("capacity", e.target.value)} />
              <FormError error={errors.capacity} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-pressure-rating`}>{t("inventory.form.pressureRatingLabel")}</Label>
              <Input id={`${idPrefix}-pressure-rating`} type="number" step="0.01" min="0" value={data.pressure_rating} onChange={(e) => onChange("pressure_rating", e.target.value)} />
              <FormError error={errors.pressure_rating} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-last-inspection`}>{t("inventory.form.lastInspectionDateLabel")}</Label>
              <Input id={`${idPrefix}-last-inspection`} type="date" value={data.last_inspection_date} onChange={(e) => onChange("last_inspection_date", e.target.value)} />
              <FormError error={errors.last_inspection_date} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-expiry`}>{t("inventory.form.expiryDateLabel")}</Label>
              <Input id={`${idPrefix}-expiry`} type="date" value={data.expiry_date} onChange={(e) => onChange("expiry_date", e.target.value)} />
              <FormError error={errors.expiry_date} />
            </div>
          </div>
        </>
      )}

      {/* BeerTap-specific */}
      {assetType === "BeerTap" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-brand`}>{t("inventory.form.brandLabel")}</Label>
              <Input id={`${idPrefix}-brand`} value={data.brand} onChange={(e) => onChange("brand", e.target.value)} />
              <FormError error={errors.brand} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-model`}>{t("inventory.form.modelLabel")}</Label>
              <Input id={`${idPrefix}-model`} value={data.model} onChange={(e) => onChange("model", e.target.value)} />
              <FormError error={errors.model} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-serial-number`}>{t("inventory.form.serialNumberLabel")}</Label>
              <Input id={`${idPrefix}-serial-number`} value={data.serial_number} onChange={(e) => onChange("serial_number", e.target.value)} />
              <FormError error={errors.serial_number} />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-tap-count`}>{t("inventory.form.tapCountLabel")}</Label>
              <Input id={`${idPrefix}-tap-count`} type="number" min="1" value={data.tap_count} onChange={(e) => onChange("tap_count", e.target.value)} />
              <FormError error={errors.tap_count} />
            </div>
          </div>
        </>
      )}

      {/* Tags - Full Width */}
      <div className="space-y-2">
        <Label>{t("inventory.form.tagsLabel")}</Label>
        <TagMultiSelect
          tags={tags}
          selectedIds={data.tag_ids}
          onChange={(ids) => onChange("tag_ids", ids)}
        />
      </div>

      {/* Notes - Full Width */}
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-notes`}>{t("inventory.form.notesLabel")}</Label>
        <textarea
          id={`${idPrefix}-notes`}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50"
          value={data.notes}
          onChange={(e) => onChange("notes", e.target.value)}
          placeholder={t("inventory.form.notesPlaceholder")}
        />
        <FormError error={errors.notes} />
      </div>
    </div>
  )
}
